import { type OrderIngredient } from '@hungryroot/orders';
import { type ProductV2 } from '@hungryroot/products';
import {
  type PairingDetail,
  type PairingV1 as OrderPairing,
  type ProductV1 as Product,
  type CartPairing,
  type CartPairingIngredient,
  type CartProduct,
  type PairingV2,
} from '@hungryroot/rest';

export function convertProductToCartProduct(product: Product): CartProduct {
  const {
    brand_name,
    card_image_url,
    cook_time,
    description,
    id,
    is_ambient,
    is_breakfast,
    is_fruit,
    is_new,
    is_snack,
    is_sweet,
    large_image2_url,
    large_image_url,
    name,
    packaged_image2_url,
    packaged_image_url,
    points,
    cost,
    servings,
    sku,
    slug,
    small_image2_url,
    small_image_url,
    sort_order,
    spicy_level,
    thumbnail_img_url,
    category_name,
    volume,
  } = product;

  return {
    brand_name,
    card_image_url,
    cook_time,
    description,
    id,
    is_ambient,
    is_breakfast,
    is_fruit,
    is_new,
    is_snack,
    is_sweet: is_sweet === undefined ? false : is_sweet,
    large_image2_url,
    large_image_url,
    name,
    packaged_image2_url,
    packaged_image_url,
    servings,
    sku,
    slug,
    small_image2_url,
    small_image_url,
    sort_order,
    spicy_level: typeof spicy_level === 'number' ? spicy_level : 0,
    thumbnail_image_url: thumbnail_img_url,
    volume,

    // Properties here are not available to convert
    nutrition_image_url: null,
    pack_order: 0,
    points: `${points}`,
    price: `${cost}`,
    rating: '0.00',
    short_name: '',
    tags: [],
    weight: 0,
    categories: [
      {
        name: category_name,
        slug: '',
        large_image_url: '',
        small_image_url: '',
        thumbnail_url: '',
        id: -100,
      },
    ],
  };
}

export function convertCartPairingToOrderPairing(
  pairing: CartPairing,
  ingredients: CartProduct[]
): OrderPairing {
  const {
    id,
    servings,
    calories,
    name,
    slug,
    short_instruction_html,
    is_active,
    is_featured,
    featured_img_url,
    featured_quote,
    sort_order,
    recipe_score,
    cooking_time,
    spicy_level,
    tags,
    points,
    num_ratings,
    average_rating,
    dish_type,
    needed_ingredients,
    badges,
  } = pairing;
  const orderPairing: OrderPairing = {
    id,
    servings,
    calories,
    name,
    slug,
    short_instruction_html,
    is_active,
    is_featured,
    featured_img: featured_img_url,
    featured_quote,
    sort_order,
    recipe_score,
    cooking_time,
    spicy_level,
    points: parseFloat(points),
    dish_type: {
      ...dish_type,
      sort_order: 10,
    },
    ingredients: ingredients.map((product) => {
      const { id: productId } = product;
      let neededIngredient = needed_ingredients.find(
        ({ ingredient_id: ingredientId }) => {
          return ingredientId === product.id;
        }
      );
      if (neededIngredient === undefined) {
        neededIngredient = {
          amount: 1,
          ingredient_id: productId,
        };
      }

      return {
        id: product.id,
        name: product.name,
        slug: product.slug,
        brand_name: product.brand_name,
        packaged_image_url: product.packaged_image_url,
        thumbnail_image_url: product.thumbnail_image_url,
        small_image2_url: product.small_image2_url,
        small_image_url: product.small_image_url,
        card_image_url:
          product.card_image_url === null ? '' : product.card_image_url,
        amount: neededIngredient.amount,
        points: parseFloat(product.points),
        volume: product.volume,
        is_ambient: product.is_ambient,
      };
    }),
    tags: tags.map(({ id }) => id),
    average_rating,
    num_ratings,
    is_favorited: false,
    badges,
  };

  return orderPairing;
}

export function convertPairingToCartPairing(
  pairing: OrderPairing
): CartPairing {
  const {
    id: pairingId,
    servings,
    calories,
    name: pairingName,
    slug: pairingSlug,
    short_instruction_html,
    is_active,
    is_featured,
    featured_img,
    featured_quote,
    sort_order,
    recipe_score,
    cooking_time,
    spicy_level,
    points,
    dish_type,
    ingredients,
  } = pairing;

  const cartPairingIngredients: CartPairingIngredient[] = [];

  ingredients.forEach((ingredient) => {
    const { amount } = ingredient;
    for (let i = 0; i < amount; i += 1) {
      cartPairingIngredients.push({
        ...ingredient,
        description: '',
        nutrition_image_url: null,
        rating: '1',
        short_name: ingredient.name,
        sku: '',
        is_breakfast: false,
        is_fruit: false,
        is_snack: false,
        is_sweet: false,
      });
    }
  });
  return {
    id: pairingId,
    servings,
    calories,
    name: pairingName,
    slug: pairingSlug,
    short_instruction_html,
    is_active,
    is_featured,
    average_rating: '',
    num_ratings: 0,
    recipe_score:
      recipe_score === null || recipe_score === undefined ? 0 : recipe_score,
    featured_img,
    featured_quote,
    sort_order,
    cooking_time,
    points: `${points}`,
    spicy_level,
    needed_ingredients: ingredients.map(({ amount, id }) => {
      return {
        amount,
        ingredient_id: id,
      };
    }),
    dish_type: {
      ...dish_type,
      image_url: dish_type?.card_image_url,
      light_image_url: '',
      slug: '',
      survey_image_url: '',
      survey_image2_url: '',
      survey_image3_url: '',
    },
    ingredients: cartPairingIngredients,
    tags: [],
    badges: [],
    featured_img_url: null,
    nutrition_info: '',
  };
}

export function convertOrderIngredientToCartProduct(
  product: OrderIngredient
): CartProduct {
  const {
    id,
    name,
    brand_name,
    packaged_image_url,
    thumbnail_image_url,
    small_image2_url,
    small_image_url,
    card_image_url,
    points,
    volume,
    is_ambient,
    slug,
  } = product;
  return {
    brand_name,
    card_image_url,
    cook_time: 0,
    description: '',
    id,
    is_ambient,
    name,
    packaged_image_url,
    thumbnail_image_url,
    small_image2_url,
    small_image_url,
    points: `${points}`,
    volume,
    slug,

    is_breakfast: false,
    is_fruit: false,
    is_new: false,
    is_snack: false,
    is_sweet: false,
    large_image2_url: null,
    large_image_url: null,
    nutrition_image_url: null,
    pack_order: 0,
    packaged_image2_url: null,
    price: '',
    rating: '',
    servings: 0,
    short_name: null,
    sku: '',
    sort_order: 0,
    spicy_level: 0,
    tags: [],
    weight: 0,
    categories: [],
  };
}

export function convertPairingV2ToPairing(pairing: PairingV2): OrderPairing {
  const tagsThatAreBadges = pairing.tags.filter((tag) => {
    return tag.is_badge;
  });

  const badges = tagsThatAreBadges.map((tag) => {
    return tag.short_name || tag.name;
  });

  return {
    ...pairing,
    name: typeof pairing.name === 'string' ? pairing.name : '',
    slug: typeof pairing.slug === 'string' ? pairing.slug : '',
    is_active: true,
    featured_img: pairing.featured_img_url,
    calories: 0,
    is_favorited: false,
    badges,
    nutrition_html: pairing.nutrition_info,
    dish_type: {
      id: 1,
      name: 'string',
      name_plural: 'string',
      large_image_url: 'string',
      small_image_url: 'string',
      card_image_url: 'string',
      survey_image4_url: 'string',
      thumbnail_url: 'string',
      sort_order: 1,
      profile_image_url: 'string',
    },
    ingredients: pairing.ingredients.map((i) => {
      return {
        ...i,
        points: Number(i.points),
      };
    }),
    points: Number(pairing.points),
    tags: pairing.tags.map((t) => t.id),
  };
}

export function convertProductV2ToProduct(product: ProductV2): Product {
  return {
    ...product,
    thumbnail_img_url: product.thumbnail_image_url,
    points: Number(product.points),
    amount: 1,
    category_name: 'category name',
    old_category: 1,
    old_category_name: 'old category name',
  };
}

export function convertPairingDetailToPairingV1(
  pairingDetail: PairingDetail
): OrderPairing {
  return {
    average_rating: pairingDetail.average_rating,
    badges: pairingDetail.badges || [],
    calories: 0,
    cooking_time: pairingDetail.cooking_time,
    dish_type: {
      id: pairingDetail.dish_type.id || 1, // Defaulting to 1 if the dish type doesn't have an ID
      name: pairingDetail.dish_type.name || 'Unknown',
      name_plural: pairingDetail.dish_type.name_plural || 'Unknowns',
      large_image_url: 'string', // Placeholder
      small_image_url: 'string', // Placeholder
      card_image_url: 'string', // Placeholder
      survey_image4_url: 'string', // Placeholder
      thumbnail_url: 'string', // Placeholder
      sort_order: pairingDetail.sort_order,
      profile_image_url: 'string', // Placeholder
    },
    featured_img: pairingDetail.featured_img_url || null,
    featured_quote: pairingDetail.featured_quote || null,
    id: pairingDetail.id,
    ingredients: pairingDetail.ingredients.map((ingredient) => ({
      id: ingredient.id,
      name: ingredient.name,
      brand_name: ingredient.brand_name,
      packaged_image_url: ingredient.packaged_image_url,
      thumbnail_image_url: ingredient.thumbnail_image_url,
      small_image2_url: ingredient.small_image2_url,
      small_image_url: ingredient.small_image_url,
      card_image_url: ingredient.card_image_url ?? '',
      amount: 1,
      points: Number(ingredient.points),
      volume: ingredient.volume,
      is_ambient: ingredient.is_ambient,
      slug: ingredient.slug,
    })),
    is_active: true,
    is_favorited: false,
    is_featured: pairingDetail.is_featured,
    name: pairingDetail.name || '',
    num_ratings: pairingDetail.num_ratings,
    nutrition_html: pairingDetail.nutrition_info || '',
    nutrition: pairingDetail.nutrition || undefined,
    points: Number(pairingDetail.points),
    quantity: undefined,
    rating: null,
    recipe_score: pairingDetail.recipe_score || null,
    servings: pairingDetail.servings,
    short_instruction_html: pairingDetail.short_instruction_html || '',
    slug: pairingDetail.slug || '',
    sort_order: pairingDetail.sort_order,
    spicy_level: pairingDetail.spicy_level,
    tags: pairingDetail.tags.map((tag) => tag.id),
  };
}
